import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import api from '../../api/apiClient';
import type { AppNotification } from '../../hooks/useNotifications';
import { CardSkeleton } from '../design-system/EmptyState';
import { ExternalLink } from 'lucide-react';

function activityDot(type: string): string {
  const t = type.toLowerCase();
  if (t.includes('payment') || t.includes('rent'))    return 'bg-ok-500';
  if (t.includes('maintenance') || t.includes('job')) return 'bg-teal-400';
  if (t.includes('lease') || t.includes('document'))  return 'bg-info-500';
  if (t.includes('compliance') || t.includes('warn')) return 'bg-warn-500';
  if (t.includes('complaint') || t.includes('urgent'))return 'bg-coral-400';
  return 'bg-ink-400';
}

function formatRelative(isoDate: string): string {
  const diff = Date.now() - new Date(isoDate).getTime();
  const mins  = Math.floor(diff / 60_000);
  const hours = Math.floor(diff / 3_600_000);
  const days  = Math.floor(diff / 86_400_000);
  if (mins  < 2)  return 'Just now';
  if (mins  < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days  < 7)  return `${days}d ago`;
  return new Date(isoDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
}

interface RecentActivityFeedProps {
  limit?: number;
  /** Label shown above the feed */
  title?: string;
}

export const RecentActivityFeed: React.FC<RecentActivityFeedProps> = ({
  limit = 5,
  title = 'Recent Activity',
}) => {
  const navigate = useNavigate();
  const { data, isLoading } = useQuery<AppNotification[]>({
    queryKey: ['notifications', 'recent'],
    queryFn:  () => api.get<AppNotification[]>('/notifications').then(r => r ?? []),
    staleTime: 30_000,
  });

  const items = (data ?? []).slice(0, limit);

  return (
    <div className="bg-card rounded-12 border border-ink-200 shadow-xs p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-[13px] font-semibold text-ink-700 uppercase tracking-wider">{title}</h2>
        <button
          onClick={() => navigate('/notifications')}
          className="flex items-center gap-1 text-[12px] text-forest-900 font-medium hover:underline"
        >
          View all <ExternalLink size={11} />
        </button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <p className="text-[13px] text-ink-500 py-4 text-center">No recent activity</p>
      ) : (
        <div className="space-y-0">
          {items.map(item => (
            <button
              key={item.id}
              onClick={() => item.actionUrl ? navigate(item.actionUrl) : undefined}
              className={`w-full flex items-start gap-3 py-2.5 border-b border-ink-100 last:border-0 text-left transition-colors ${item.actionUrl ? 'hover:bg-ink-50 rounded-lg px-1 -mx-1 cursor-pointer' : 'cursor-default'}`}
            >
              <div className={`w-2 h-2 rounded-full flex-none mt-1.5 ${activityDot(item.type)}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-[13px] ${item.isRead ? 'text-ink-700' : 'text-ink-900 font-medium'} truncate`}>
                  {item.title}
                </p>
                {item.message && (
                  <p className="text-[11px] text-ink-500 truncate mt-0.5">{item.message}</p>
                )}
              </div>
              <span className="text-[11px] text-ink-400 flex-none mt-0.5 tabular-nums">
                {formatRelative(item.createdAt)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
